import * as React from 'react'

import PerformanceBreakdownView from './performanceBreakdownView'
import SmartGroupsView from './smartGroupsView'
import {
  ADMIN_STANDARDS_REPORT_MOMENTUM_SCORE_GOAL,
  AGGREGATION_STANDARD,
  AGGREGATION_STUDENT,
  ALL_CATEGORIES_OPTION,
  ALL_GRADE_LEVELS_OPTION,
  ALL_STUDENTS_OPTION,
  PERFORMANCE_BREAKDOWN_VIEW,
  ResultRow,
  SMART_GROUPS_VIEW,
  SkillCategory,
  StateStandard,
  StateStandardsFramework,
  VIEW_LABEL_BY_VALUE,
  enrichStandardRows,
  groupByDropdownOptions,
  payloadMatchesHash,
  supplementMissingRows,
} from './helpers'

import {
  ReportHeaderButtons,
  ReportViewToggle,
  SelectTeacherPrompt,
  StudentFilterDropdown,
  useMomentumScoreGoalModal,
} from '../../adminMomentumReports/components'
import {
  useAppliedTeacher,
  usePusherMessage,
  useSnackbar,
  useTeacherStudents,
} from '../../adminMomentumReports/hooks'
import ReportStandardsModal from '../../../Teacher/components/progress_reports/standards_report/report_standards_modal'
import { ALL_GRADE_LEVELS_FOR_DROPDOWN, formatGradeLevelWithContext, } from '../../../Teacher/components/progress_reports/standards_report/shared'
import { ADMIN_SKILLS_REPORT_PATH, FULL, forwardSlash, restrictedPage, mapItemsIfNotAll, } from '../../shared'
import {
  DropdownInput,
  Snackbar,
  Spinner,
  Tooltip,
  filterIcon,
  getPageTitle,
} from '../../../Shared/index'
import { DropdownObjectInterface } from '../../../Staff/interfaces/evidenceInterfaces'
import { requestGet, requestPost, requestPut, } from '../../../../modules/request'

const QUERY_ENDPOINT = '/admin_standards_reports/report'
const STANDARDS_DATA_ENDPOINT = '/admin_standards_reports/standards_data'
const STUDENTS_ENDPOINT = '/admin_standards_reports/students'
const PUSHER_EVENT_KEY = 'admin-standards-report-cached'

const AdminStandardsReportsContainer = ({
  accessType,
  loadingFilters,
  pusherChannel,
  searchCount,
  selectedClassrooms,
  availableClassrooms,
  selectedTimeframe,
  selectedGrades,
  availableGrades,
  selectedSchools,
  selectedTeachers,
  availableTeachers,
  applyTeacherSelection,
  openMobileFilterMenu,
  horizontalFilters,
}) => {
  document.title = getPageTitle('Premium Hub', 'Premium Reports | Standards Report')

  const [groupByValue, setGroupByValue] = React.useState<DropdownObjectInterface>(groupByDropdownOptions[0])
  const [activeView, setActiveView] = React.useState<string>(SMART_GROUPS_VIEW)
  const [rows, setRows] = React.useState<ResultRow[] | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)

  const [selectedStateStandardsFramework, setSelectedStateStandardsFramework] = React.useState<StateStandardsFramework | null>(null)
  const [allStateStandardsFrameworks, setAllStateStandardsFrameworks] = React.useState<StateStandardsFramework[]>([])
  const [allStateStandards, setAllStateStandards] = React.useState<StateStandard[]>([])
  const [allSkillCategories, setAllSkillCategories] = React.useState<SkillCategory[]>([])
  const [loadingStandardsData, setLoadingStandardsData] = React.useState<boolean>(true)
  const [practicedStandardIds, setPracticedStandardIds] = React.useState<Set<string>>(new Set())

  const [gradeLevelValue, setGradeLevelValue] = React.useState<DropdownObjectInterface>(ALL_GRADE_LEVELS_OPTION)
  const [skillCategoryValue, setSkillCategoryValue] = React.useState<DropdownObjectInterface>(ALL_CATEGORIES_OPTION)
  const [selectedStandardValue, setSelectedStandardValue] = React.useState<DropdownObjectInterface | null>(null)
  const [showReportStandardsModal, setShowReportStandardsModal] = React.useState(false)

  const pusherMessage = usePusherMessage(pusherChannel, PUSHER_EVENT_KEY)
  const { appliedTeacherId, singleTeacherApplied } = useAppliedTeacher(selectedTeachers, availableTeachers, searchCount)
  const { studentFilterValue, setStudentFilterValue, studentOptions, studentsLoading } = useTeacherStudents(appliedTeacherId, STUDENTS_ENDPOINT)
  const { showSnackbar, snackbarText, displaySnackbar } = useSnackbar()
  const { momentumScoreGoal, openGoalModal, goalModal } = useMomentumScoreGoalModal(ADMIN_STANDARDS_REPORT_MOMENTUM_SCORE_GOAL, displaySnackbar)

  const isStandardAggregation = groupByValue?.value === AGGREGATION_STANDARD
  const standardGradeLevel = gradeLevelValue.value === ALL_GRADE_LEVELS_OPTION.value ? null : gradeLevelValue.value
  const isSpecificStudent = studentFilterValue.value !== ALL_STUDENTS_OPTION.value
  const conceptId = skillCategoryValue.value === ALL_CATEGORIES_OPTION.value ? null : Number(skillCategoryValue.value)
  const requiresTeacherSelection = groupByValue?.value === AGGREGATION_STUDENT
  const hasSingleTeacherSelected = selectedTeachers && selectedTeachers.length === 1
  const studentViewBlocked = requiresTeacherSelection && !hasSingleTeacherSelected

  function fetchStandardsData(frameworkId: number | null, setFrameworkFromResponse: boolean) {
    setLoadingStandardsData(true)
    const url = frameworkId ? `${STANDARDS_DATA_ENDPOINT}?state_standards_framework_id=${frameworkId}` : STANDARDS_DATA_ENDPOINT
    requestGet(url, (body) => {
      if (Array.isArray(body?.allStateStandardsFrameworks)) setAllStateStandardsFrameworks(body.allStateStandardsFrameworks)
      if (Array.isArray(body?.allStateStandards)) setAllStateStandards(body.allStateStandards)
      if (Array.isArray(body?.allSkillCategories)) setAllSkillCategories(body.allSkillCategories)
      if (setFrameworkFromResponse && body?.selectedStateStandardsFramework) {
        setSelectedStateStandardsFramework(body.selectedStateStandardsFramework)
      }
      setLoadingStandardsData(false)
    })
  }

  React.useEffect(() => { fetchStandardsData(null, true) }, [])

  const gradesWithStandards = React.useMemo(() => new Set(allStateStandards.flatMap((s) => s.grades || [])), [allStateStandards])

  const gradeLevelOptions = React.useMemo(() => [
    ALL_GRADE_LEVELS_OPTION,
    ...ALL_GRADE_LEVELS_FOR_DROPDOWN.map((grade) => ({
      label: formatGradeLevelWithContext(grade, true),
      value: grade,
      isDisabled: !gradesWithStandards.has(grade),
    })),
  ], [gradesWithStandards])

  const skillCategoryOptions = React.useMemo(() => {
    const matchesGrade = (category: SkillCategory) => !standardGradeLevel || category.grades?.includes(standardGradeLevel)
    return [
      ALL_CATEGORIES_OPTION,
      ...allSkillCategories.map((category) => ({
        label: category.name,
        value: String(category.id),
        isDisabled: !matchesGrade(category),
      })),
    ]
  }, [allSkillCategories, standardGradeLevel])

  const standardOptions = React.useMemo(() => {
    const hasPracticeData = practicedStandardIds.size > 0
    return allStateStandards
      .filter((standard) => !standardGradeLevel || standard.grades?.includes(standardGradeLevel))
      .map((standard) => ({
        label: `${standard.code} — ${standard.description}`,
        value: String(standard.id),
        // Everything stays enabled until the by-standard report reveals practice data.
        isDisabled: hasPracticeData && !practicedStandardIds.has(String(standard.id)),
      }))
      .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled))
  }, [allStateStandards, standardGradeLevel, practicedStandardIds])

  // The user's pick while it remains a valid enabled option, otherwise the first
  // enabled one. Derived rather than repaired via an effect so the report fetch
  // never runs against a transiently-stale selection.
  const effectiveStandardOption = React.useMemo(() => {
    if (isStandardAggregation || !standardOptions.length) return null
    return standardOptions.find((o) => o.value === selectedStandardValue?.value && !o.isDisabled) ||
      standardOptions.find((o) => !o.isDisabled) ||
      standardOptions[0]
  }, [isStandardAggregation, standardOptions, selectedStandardValue?.value])

  const selectedStandardId = effectiveStandardOption ? Number(effectiveStandardOption.value) : null
  const selectedStandard = allStateStandards.find((standard) => standard.id === selectedStandardId) || null

  // Masked by aggregation exactly as the payload applies them, so the fetch
  // effect only re-fires when the outgoing payload changes.
  const appliedStandardGradeLevel = isStandardAggregation ? standardGradeLevel : null
  const appliedConceptId = isStandardAggregation ? conceptId : null
  const appliedStudentId = isStandardAggregation && isSpecificStudent ? Number(studentFilterValue.value) : null

  React.useEffect(() => {
    if (!groupByValue) return
    if (!selectedStateStandardsFramework?.id) return
    if (studentViewBlocked) { setRows([]); setLoading(false); return }
    if (!isStandardAggregation && !selectedStandardId) { setRows([]); setLoading(false); return }
    fetchReport()
  }, [searchCount, groupByValue, appliedStandardGradeLevel, appliedConceptId, selectedStandardId, appliedStudentId, selectedStateStandardsFramework?.id])

  React.useEffect(() => {
    if (!pusherMessage) return
    if (filtersMatchHash(pusherMessage)) fetchReport()
  }, [pusherMessage])

  function filterPayload() {
    return {
      aggregation: groupByValue.value,
      timeframe: selectedTimeframe.value,
      school_ids: selectedSchools.map((s) => s.id),
      grades: mapItemsIfNotAll(selectedGrades, availableGrades, 'value'),
      teacher_ids: mapItemsIfNotAll(selectedTeachers, availableTeachers),
      classroom_ids: mapItemsIfNotAll(selectedClassrooms, availableClassrooms),
      state_standards_framework_id: selectedStateStandardsFramework?.id,
      state_standard_id: selectedStandardId,
      standard_grade_level: appliedStandardGradeLevel,
      concept_id: appliedConceptId,
      student_id: appliedStudentId,
    }
  }

  function fetchReport() {
    setLoading(true)
    requestPost(QUERY_ENDPOINT, filterPayload(), (body) => {
      if (body?.results) {
        const results = body.results as ResultRow[]
        setRows(results)
        if (isStandardAggregation) {
          setPracticedStandardIds(new Set(results.map((r) => String(r.aggregate_id))))
        }
        setLoading(false)
      }
      // Otherwise the backend enqueued a worker — Pusher signals completion.
    })
  }

  // filterPayload's literal key order mirrors the Sidekiq worker's PayloadHasher
  // inputs: aggregation, timeframe, school_ids, then
  // AdminStandardsReport::PerformanceWorker::HASHED_FILTER_KEYS.
  function filtersMatchHash(hashMessage: string) {
    return payloadMatchesHash(Object.values(filterPayload()), hashMessage)
  }

  function handleGradeLevelChange(option: DropdownObjectInterface) {
    setGradeLevelValue(option)
    setSkillCategoryValue(ALL_CATEGORIES_OPTION)
  }

  function handleClickChangeFramework() { setShowReportStandardsModal(true) }
  function handleViewPerformanceBreakdown() { setActiveView(PERFORMANCE_BREAKDOWN_VIEW) }

  function handleCloseReportStandardsModal(snackbarMessage?: string) {
    setShowReportStandardsModal(false)
    if (typeof snackbarMessage === 'string') displaySnackbar(snackbarMessage)
  }

  function handleChangeSelectedStateStandardsFramework(newFramework: StateStandardsFramework) {
    setSelectedStateStandardsFramework(newFramework)
    setGradeLevelValue(ALL_GRADE_LEVELS_OPTION)
    setSkillCategoryValue(ALL_CATEGORIES_OPTION)
    setSelectedStandardValue(null)
    setStudentFilterValue(ALL_STUDENTS_OPTION)
    setPracticedStandardIds(new Set())
    setRows(null)
    // Shared preference: also updates the teacher Standards Report.
    requestPut('/teacher_infos', { state_standards_framework_id: newFramework.id })
    fetchStandardsData(newFramework.id, false)
  }

  function handleSelectSkillCategoryFromRow(categoryName: string) {
    const category = allSkillCategories.find((c) => c.name === categoryName)
    const params = category ? `?skill_category_id=${category.id}` : ''
    window.open(`${ADMIN_SKILLS_REPORT_PATH}${params}`, '_blank', 'noopener')
  }

  function handleSelectStudentFromRow(studentId: number | string, studentName: string) {
    setGroupByValue(groupByDropdownOptions.find((o) => o.value === AGGREGATION_STANDARD))
    setStudentFilterValue({ label: studentName, value: String(studentId) })
  }

  const isStudentFilterDisabled = !singleTeacherApplied || studentsLoading

  const displayRows = React.useMemo(() => {
    if (!rows) return rows
    if (isStandardAggregation) {
      const enriched = enrichStandardRows(rows, allStateStandards)
      return supplementMissingRows({ rows: enriched, aggregation: groupByValue.value, allStateStandards, standardGradeLevel })
    }
    return supplementMissingRows({ rows, aggregation: groupByValue.value, allStateStandards, standardGradeLevel: null, selectedGrades, selectedTeachers })
  }, [rows, groupByValue.value, allStateStandards, standardGradeLevel, selectedGrades, selectedTeachers, isStandardAggregation])

  function renderBody() {
    if (studentViewBlocked) {
      return <SelectTeacherPrompt applyTeacherSelection={applyTeacherSelection} availableTeachers={availableTeachers} />
    }

    if (!isStandardAggregation && !selectedStandardId) {
      return (
        <div className="empty-state">
          <h2>No standards with practice data for the selected filters</h2>
          <p>Adjust your filters or check back once students have completed more practice.</p>
        </div>
      )
    }

    if (loading || !displayRows) return <Spinner />

    if (activeView === SMART_GROUPS_VIEW) {
      return (
        <SmartGroupsView
          aggregation={groupByValue.value}
          goal={momentumScoreGoal}
          isSpecificStudent={isSpecificStudent}
          onViewPerformanceBreakdown={handleViewPerformanceBreakdown}
          rows={displayRows}
        />
      )
    }

    return (
      <PerformanceBreakdownView
        aggregation={groupByValue.value}
        goal={momentumScoreGoal}
        isSpecificStudent={isSpecificStudent}
        onSelectSkillCategory={handleSelectSkillCategoryFromRow}
        onSelectStudent={handleSelectStudentFromRow}
        practicedStandardsOnly={isStandardAggregation && !standardGradeLevel}
        rows={displayRows}
      />
    )
  }

  if (loadingFilters || loadingStandardsData) return <Spinner />
  if (accessType !== FULL) return restrictedPage

  return (
    <main className="admin-skills-report admin-standards-report">
      <div className="header">
        <h1>
          <span>Standards Report</span>
          <span className="header-breadcrumb">
            {forwardSlash}
            {VIEW_LABEL_BY_VALUE[activeView]}
            {forwardSlash}
            {`By ${groupByValue.label}`}
            {isStandardAggregation && skillCategoryValue.value !== ALL_CATEGORIES_OPTION.value && (
              <React.Fragment>
                {forwardSlash}
                {skillCategoryValue.label}
              </React.Fragment>
            )}
            {selectedStandard && (
              <React.Fragment>
                {forwardSlash}
                <Tooltip tooltipText={selectedStandard.description} tooltipTriggerText={selectedStandard.code} />
              </React.Fragment>
            )}
          </span>
          {selectedStateStandardsFramework && (
            <Tooltip
              tooltipText={`This report is aligned to ${selectedStateStandardsFramework.name}. Click to change.`}
              tooltipTriggerText={(
                <button className="quill-button outlined green extra-small focus-on-light framework-chip" onClick={handleClickChangeFramework} type="button">
                  {selectedStateStandardsFramework.abbreviation}
                </button>
              )}
            />
          )}
        </h1>
        <ReportHeaderButtons
          guideUrl="https://support.quill.org/en/articles/15926304-how-do-i-read-the-admin-standards-report-quill-premium"
          onClickChangeGoal={openGoalModal}
        />
      </div>
      <div className="view-toggle-wrapper">
        <ReportViewToggle activeView={activeView} dropdownId="admin-standards-view-toggle" onChangeView={setActiveView} />
      </div>
      <div className="dropdowns-container">
        <DropdownInput
          className="group-by-dropdown bordered small focus-on-light"
          handleChange={setGroupByValue}
          isSearchable={true}
          label="Group by:"
          options={groupByDropdownOptions}
          value={groupByValue}
        />
        <div className="right-controls">
          <DropdownInput
            className="grade-level-dropdown bordered small focus-on-light"
            handleChange={handleGradeLevelChange}
            isSearchable={true}
            label="Standard Grade Level"
            options={gradeLevelOptions}
            value={gradeLevelOptions.find((o) => o.value === gradeLevelValue.value)}
          />
          {isStandardAggregation ? (
            <React.Fragment>
              <DropdownInput
                className="skill-category-dropdown bordered small focus-on-light"
                handleChange={setSkillCategoryValue}
                isSearchable={true}
                label="Skill Category"
                options={skillCategoryOptions}
                value={skillCategoryOptions.find((o) => o.value === skillCategoryValue.value)}
              />
              <StudentFilterDropdown
                disabled={isStudentFilterDisabled}
                onChange={setStudentFilterValue}
                options={studentOptions}
                value={studentFilterValue}
              />
            </React.Fragment>
          ) : (
            <DropdownInput
              className="standard-dropdown bordered small focus-on-light"
              disabled={!standardOptions.length}
              handleChange={setSelectedStandardValue}
              isSearchable={true}
              label="Standard"
              options={standardOptions}
              placeholder={standardOptions.length ? null : 'None available'}
              value={effectiveStandardOption}
            />
          )}
        </div>
      </div>
      <div className="filter-button-container">
        <button className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
          <img alt={filterIcon.alt} src={filterIcon.src} />
          Filters
        </button>
      </div>
      {horizontalFilters}
      {renderBody()}
      {goalModal}
      {showReportStandardsModal && selectedStateStandardsFramework && (
        <ReportStandardsModal
          allStateStandardsFrameworks={allStateStandardsFrameworks}
          closeModal={handleCloseReportStandardsModal}
          handleChangeSelectedStateStandardsFramework={handleChangeSelectedStateStandardsFramework}
          selectedStateStandardsFramework={selectedStateStandardsFramework}
        />
      )}
      <Snackbar text={snackbarText} visible={showSnackbar} />
    </main>
  )
}

export default AdminStandardsReportsContainer
